import { useEffect, useRef, useState } from 'react';
import { useFlags } from 'flagsmith/react';
import { Card, Checkbox, Col, Form, Input, Radio, Row, Typography } from 'antd';
import { VEHICLE_FIELDS } from '@features/vehicles/constants/vehicleFields';
import { SelectPlus } from 'Components/SelectPlus';
import { InputPlate } from './InputPlate';
import { InputFasecoldaCode } from './InputFasecoldaCode';
import { SelectServiceType } from '../SelectServiceType';
import { SelectMunicipality } from '@features/locations/components/SelectMunicipality';
import { InputCurrency } from '@shared/components/inputs/InputCurrency';
import {
  useGetCategories,
  useGetVehicleDataModels,
  useGetVehicleDataBrands,
  useSearchVehicles,
  useGetVehicleByCode,
} from '@features/vehicles/hooks/useVehicleData';
import { VehicleFormFallback } from './fallback';

const { Text } = Typography;

const CLASE_TO_CATEGORY_NAME = {
  'AUTOMOVIL': 'Automóvil',
  'BUS / BUSETA / MICROBUS': 'Pesados',
  'CAMION': 'Pesados',
  'CAMIONETA PASAJ.': 'Automóvil',
  'CAMIONETA REPAR': 'Pickup',
  'CAMPERO': 'SUV',
  'CARROTANQUE': 'Pesados',
  'CHASIS': 'Pesados',
  'CUATRIMOTO': 'Motos',
  'FURGON': 'Pesados',
  'FURGONETA': 'Pesados',
  'ISOCARRO': 'Motos',
  'MOTOCARRO': 'Motos',
  'MOTOCICLETA': 'Motos',
  'PICKUP DOBLE CAB': 'Pickup',
  'PICKUP SENCILLA': 'Pickup',
  'REMOLCADOR': 'Pesados',
  'REMOLQUE': 'Pesados',
  'VOLQUETA': 'Pesados',
};

// Normaliza un nombre para compararlo ignorando mayúsculas y acentos
const normalizeName = (str) =>
  str?.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '') ?? '';

const AUX_VEHICLE_FIELDS = {
  UNKNOWN_FASECOLDA_CODE: 'unknownFasecoldaCode',
  CATEGORY_ID: 'categoryId',
};

const validatePlate = (value) => {
  if (!value) return Promise.resolve();

  const allowedVehiclePlateFormats = [
    /^[A-Z]{3}[0-9]{3}$/i, // ABC123 (particulares, públicos, oficiales, clásicos, antiguos, diplomáticos, consulares, misiones especiales)
    /^(CD|CC|AT|OI)[0-9]{4}$/i, // CD1234 | CC1234 | AT1234 | OI1234 (formatos antiguos: diplomáticos, consulares, misiones especiales)
    /^[A-Z]{3}[0-9]{2}[A-Z]?$/i, // ABC12 | ABC12D (motos)
    /^[RS][0-9]{5}$/i, // R12345 | S12345 (remolques, semirremolques)
  ];

  const isValid = allowedVehiclePlateFormats.some((regExp) => regExp.test(value));

  if (!isValid) return Promise.reject('El formato de la placa no es válido');

  return Promise.resolve();
};

const VehicleFormCurrent = ({ form, initialValues, cardProps = {}, onCirculationCityChange, ...formProps }) => {
  const [plateReady, setPlateReady] = useState(!!initialValues);
  const [formUnlocked, setFormUnlocked] = useState(!!initialValues);
  // Tras búsqueda por Fasecolda los campos del vehículo se bloquean visualmente:
  // el código identifica un vehículo único.
  const [unlockedByFasecolda, setUnlockedByFasecolda] = useState(false);
  // Fuerza el cascade manual (solo Categoría habilitada al inicio) tras cambiar de modo Fasecolda a manual.
  const [enforceManualCascade, setEnforceManualCascade] = useState(false);
  // IDs enteros del vehicle-data-service, solo para queries del cascade — nunca como valor del formulario.
  const [modelId, setModelId] = useState(null);
  const [brandId, setBrandId] = useState(null);
  // Distingue "primera selección" de Categoría (autocomplete no la resolvió) de un cambio real.
  const previousCategoryIdRef = useRef(initialValues?.categoryId);
  // Habilita la actualización automática de Valor Asegurado al cambiar Nuevo/Usado.
  const userTouchedIsNewRef = useRef(false);

  const hasPlate = Form.useWatch(VEHICLE_FIELDS.HAS_PLATE, form);
  const plate = Form.useWatch(VEHICLE_FIELDS.PLATE, form);
  const unknownFasecoldaCode = Form.useWatch(AUX_VEHICLE_FIELDS.UNKNOWN_FASECOLDA_CODE, form);
  const categoryId = Form.useWatch(AUX_VEHICLE_FIELDS.CATEGORY_ID, form);
  const isNew = Form.useWatch(VEHICLE_FIELDS.IS_NEW, form);
  const stateId = isNew === true ? 1 : isNew === false ? 0 : undefined;
  const model = Form.useWatch(VEHICLE_FIELDS.MODEL, form);
  const brand = Form.useWatch(VEHICLE_FIELDS.BRAND, form);
  const line = Form.useWatch(VEHICLE_FIELDS.LINE, form);
  const [committedFasecoldaCode, setCommittedFasecoldaCode] = useState(initialValues?.fasecoldaCode ?? null);

  const isStateSelected = isNew !== undefined && isNew !== null;
  const manualMode = !hasPlate && unknownFasecoldaCode;

  // Un campo está listo si el form se desbloqueó por búsqueda, o si estamos en manual
  // y la dependencia previa ya fue seleccionada.
  // enforceManualCascade anula formUnlocked para forzar el cascade cuando el usuario
  // cambia de modo Fasecolda a manual después de haber buscado.
  const fieldReady = (dependencyMet) => {
    if (enforceManualCascade) return dependencyMet;
    return formUnlocked || (manualMode && dependencyMet);
  };

  const categoriesQuery = useGetCategories();
  const modelsQuery = useGetVehicleDataModels(
    { categoryId, stateId },
    { enabled: !!categoryId && Number.isInteger(categoryId) && stateId !== undefined },
  );
  const brandsQuery = useGetVehicleDataBrands(
    { categoryId, stateId, modelId },
    { enabled: !!modelId },
  );
  const vehiclesSearchQuery = useSearchVehicles(
    { brandId, modelId, categoryId, stateId },
    { enabled: !!brandId && !!modelId && !!categoryId && Number.isInteger(categoryId) },
  );

  const vehicleEnrichmentQuery = useGetVehicleByCode(committedFasecoldaCode, {
    enabled: !!committedFasecoldaCode,
  });

  useEffect(() => {
    if (!vehicleEnrichmentQuery.data) return;
    const enriched = vehicleEnrichmentQuery.data;

    // Categoría: resolver si aún no se asignó.
    if (categoriesQuery.data && !form.getFieldValue(AUX_VEHICLE_FIELDS.CATEGORY_ID)) {
      const categoryData = resolveCategoryData(enriched);
      if (categoryData) {
        form.setFieldsValue({ [AUX_VEHICLE_FIELDS.CATEGORY_ID]: categoryData.id });
      }
    }

    // Valor Asegurado: actualizar siempre si el usuario tocó Estado; si no, solo llenar vacío.
    if (enriched.modelValues?.length) {
      const currentModel = form.getFieldValue(VEHICLE_FIELDS.MODEL);
      const modelEntry = (currentModel
        ? enriched.modelValues.find((mv) => String(mv.model) === String(currentModel))
        : undefined) ?? pickModelEntry(enriched.modelValues, isNew);
      const isEmpty = !form.getFieldValue(VEHICLE_FIELDS.INSURED_VALUE);
      if (modelEntry?.insuredValue && (userTouchedIsNewRef.current || isEmpty)) {
        form.setFieldsValue({ [VEHICLE_FIELDS.INSURED_VALUE]: modelEntry.insuredValue });
      }
    }
  }, [vehicleEnrichmentQuery.data, categoriesQuery.data, isNew]);

  // Deriva el integer ID del modelo desde el texto del año — solo para habilitar brandsQuery.
  useEffect(() => {
    if (!modelsQuery.data || !model) { setModelId(null); return; }
    const match = modelsQuery.data.find((m) => m.name === String(model));
    setModelId(match?.id ?? null);
  }, [modelsQuery.data, model]);

  // Deriva el integer ID de la marca — solo para habilitar vehiclesSearchQuery.
  useEffect(() => {
    if (!brandsQuery.data || !brand) { setBrandId(null); return; }
    // Preferir match por ID directo cuando el API Fasecolda lo retorna.
    const enrichmentBrandId = vehicleEnrichmentQuery.data?.brandId;
    if (enrichmentBrandId) {
      const idMatch = brandsQuery.data.find((b) => b.id === enrichmentBrandId);
      if (idMatch) { setBrandId(idMatch.id); return; }
    }
    const nameMatch = brandsQuery.data.find((b) => normalizeName(b.name) === normalizeName(brand));
    setBrandId(nameMatch?.id ?? null);
  }, [brandsQuery.data, brand, vehicleEnrichmentQuery.data]);

  useEffect(() => { previousCategoryIdRef.current = categoryId; }, [categoryId]);

  const categoryOptions = categoriesQuery.data?.map((c) => ({ label: c.name, value: c.id })) ?? [];
  const modelOptions = modelsQuery.data?.map((m) => ({ label: m.name, value: m.name })) ?? [];
  const brandOptions = brandsQuery.data?.map((b) => ({ label: b.name, value: b.name })) ?? [];
  const lineOptions = vehiclesSearchQuery.data?.map((v) => ({ label: v.line, value: v.line, record: v })) ?? [];

  const handleValuesChange = (changedValues, allValues) => {
    if (VEHICLE_FIELDS.HAS_PLATE in changedValues) {
      form.setFieldsValue({
        [VEHICLE_FIELDS.PLATE]: allValues[VEHICLE_FIELDS.HAS_PLATE] ? '' : 'XXX000',
      });
    }

    if (AUX_VEHICLE_FIELDS.UNKNOWN_FASECOLDA_CODE in changedValues) {
      if (changedValues[AUX_VEHICLE_FIELDS.UNKNOWN_FASECOLDA_CODE]) {
        setVehicleToForm();
        setEnforceManualCascade(true);
        setUnlockedByFasecolda(false);
        setCommittedFasecoldaCode(null);
      } else {
        // Al desmarcar el checkbox: limpiar campos y bloquear todo hasta nueva búsqueda Fasecolda.
        setVehicleToForm();
        setFormUnlocked(false);
        setEnforceManualCascade(false);
        setCommittedFasecoldaCode(null);
      }
    }

    if (AUX_VEHICLE_FIELDS.CATEGORY_ID in changedValues) {
      const newCategoryId = changedValues[AUX_VEHICLE_FIELDS.CATEGORY_ID];
      const category = categoriesQuery.data?.find((c) => c.id === newCategoryId);
      const wasUnselected = previousCategoryIdRef.current === undefined || previousCategoryIdRef.current === null;
      form.setFieldsValue({ [VEHICLE_FIELDS.CATEGORY]: category?.name });
      if (!wasUnselected) {
        form.setFieldsValue({
          [VEHICLE_FIELDS.MODEL]: undefined,
          [VEHICLE_FIELDS.BRAND]: undefined,
          [VEHICLE_FIELDS.LINE]: undefined,
          [VEHICLE_FIELDS.CLASS]: undefined,
          [VEHICLE_FIELDS.FASECOLDA_CODE]: undefined,
          [VEHICLE_FIELDS.INSURED_VALUE]: undefined,
        });
      }
    }

    if (VEHICLE_FIELDS.IS_NEW in changedValues) {
      userTouchedIsNewRef.current = true;
    }

    if (VEHICLE_FIELDS.MODEL in changedValues) {
      if (unlockedByFasecolda) {
        // En modo Fasecolda, marca/referencia/clase vienen del código y no deben resetearse.
        // Solo se actualiza el valor asegurado según el año seleccionado.
        const selectedModel = changedValues[VEHICLE_FIELDS.MODEL];
        const modelEntry = vehicleEnrichmentQuery.data?.modelValues?.find(
          (mv) => mv.model === String(selectedModel),
        );
        if (modelEntry?.insuredValue) {
          form.setFieldsValue({ [VEHICLE_FIELDS.INSURED_VALUE]: modelEntry.insuredValue });
        }
      } else {
        form.setFieldsValue({
          [VEHICLE_FIELDS.BRAND]: undefined,
          [VEHICLE_FIELDS.LINE]: undefined,
          [VEHICLE_FIELDS.CLASS]: undefined,
          [VEHICLE_FIELDS.FASECOLDA_CODE]: undefined,
        });
      }
    }

    if (VEHICLE_FIELDS.BRAND in changedValues) {
      form.setFieldsValue({
        [VEHICLE_FIELDS.LINE]: undefined,
        [VEHICLE_FIELDS.CLASS]: undefined,
        [VEHICLE_FIELDS.FASECOLDA_CODE]: undefined,
      });
    }

    if (VEHICLE_FIELDS.LINE in changedValues) {
      const line = changedValues[VEHICLE_FIELDS.LINE];
      if (line) {
        const vehicle = vehiclesSearchQuery.data?.find((v) => v.line === line);
        const modelEntry = vehicle?.modelValues?.length
          ? pickModelEntry(vehicle.modelValues, isNew)
          : undefined;
        form.setFieldsValue({
          [VEHICLE_FIELDS.FASECOLDA_CODE]: vehicle?.fasecoldaCode,
          [VEHICLE_FIELDS.CLASS]: vehicle?.class,
          [VEHICLE_FIELDS.INSURED_VALUE]: modelEntry?.insuredValue,
        });
      } else {
        form.setFieldsValue({
          [VEHICLE_FIELDS.FASECOLDA_CODE]: undefined,
          [VEHICLE_FIELDS.CLASS]: undefined,
          [VEHICLE_FIELDS.INSURED_VALUE]: undefined,
        });
      }
    }
  };

  const handleVehicleByPlateQuery = ({ data, isFetching, isError }) => {
    if (isFetching) {
      setPlateReady(false);
      return;
    }

    if (!isError) {
      setVehicleToForm(data);
      // setFieldsValue no dispara onValuesChange, así el handler no limpia FASECOLDA_CODE.
      form.setFieldsValue({ [AUX_VEHICLE_FIELDS.UNKNOWN_FASECOLDA_CODE]: true });
      setFormUnlocked(true);
      setEnforceManualCascade(false);
      setUnlockedByFasecolda(false);
      setCommittedFasecoldaCode(data?.fasecoldaCode ?? null);
    } else {
      setVehicleToForm();
      form.setFieldsValue({ [AUX_VEHICLE_FIELDS.UNKNOWN_FASECOLDA_CODE]: false });
      setCommittedFasecoldaCode(null);
    }

    setPlateReady(true);
  };

  const handleVehicleByFasecoldaCodeQuery = ({ data, isFetching, isError }) => {
    if (isFetching) return;

    if (!isError && data) {
      setVehicleToForm(data);
      setFormUnlocked(true);
      setEnforceManualCascade(false);
      setUnlockedByFasecolda(true);
      setCommittedFasecoldaCode(data.fasecoldaCode ?? form.getFieldValue(VEHICLE_FIELDS.FASECOLDA_CODE));
    } else {
      setVehicleToForm({
        fasecoldaCode: form.getFieldValue(VEHICLE_FIELDS.FASECOLDA_CODE),
      });
    }
  };

  const resolveCategoryData = (vehicle) => {
    if (!categoriesQuery.data) return undefined;

    if (vehicle.category) {
      const match = categoriesQuery.data.find(
        (c) => normalizeName(c.name) === normalizeName(vehicle.category),
      );
      if (match) return match;
    }

    //    Fallback: derivar de la clase. Necesario cuando el vehículo viene de Django
    const claseEntries = Object.entries(CLASE_TO_CATEGORY_NAME)
      .sort((a, b) => b[0].length - a[0].length);

    const classNorm = normalizeName(vehicle.class ?? '');
    const categoryNameFromClass = classNorm
      ? claseEntries.find(([clase]) => classNorm.includes(normalizeName(clase)))?.[1]
      : undefined;

    if (categoryNameFromClass) {
      const match = categoriesQuery.data.find(
        (c) => normalizeName(c.name) === normalizeName(categoryNameFromClass),
      );
      if (match) return match;
    }

    //    Fallback final: algunos vehículos devuelven clase con el mismo nombre que la categoría
    if (classNorm) {
      const match = categoriesQuery.data.find(
        (c) => normalizeName(c.name) === classNorm,
      );
      if (match) return match;
    }

    return undefined;
  };

  const pickModelEntry = (modelValues, isNewTarget) =>
    (isNewTarget === true || isNewTarget === false
      ? modelValues.find((mv) => mv.isNew === isNewTarget)
      : undefined) ?? modelValues[0];

  const setVehicleToForm = (vehicle = {}) => {
    const categoryData = resolveCategoryData(vehicle);
    const modelValues = vehicle.modelValues ?? [];
    const modelEntry = modelValues.length ? pickModelEntry(modelValues, isNew) : undefined;

    form.setFieldsValue({
      [VEHICLE_FIELDS.FASECOLDA_CODE]: vehicle.fasecoldaCode || undefined,
      [VEHICLE_FIELDS.BRAND]: vehicle.brand || undefined,
      [VEHICLE_FIELDS.MODEL]: modelEntry?.model || vehicle.model || undefined,
      [VEHICLE_FIELDS.LINE]: vehicle.line || undefined,
      [VEHICLE_FIELDS.CLASS]: vehicle.class || undefined,
      [VEHICLE_FIELDS.INSURED_VALUE]: modelEntry?.insuredValue || vehicle.insuredValue || undefined,
      [AUX_VEHICLE_FIELDS.CATEGORY_ID]: categoryData?.id,
    });
  };

  return (
    <Card
      {...cardProps}
      styles={{
        ...cardProps.styles,
        header: {
          textAlign: 'center',
          ...cardProps.styles?.header,
        },
      }}
    >
      <Form
        layout='vertical'
        form={form}
        onValuesChange={handleValuesChange}
        initialValues={{
          [VEHICLE_FIELDS.HAS_PLATE]: true,
          [AUX_VEHICLE_FIELDS.UNKNOWN_FASECOLDA_CODE]: !!initialValues,
          [VEHICLE_FIELDS.IS_UTILITARIAN]: false,
          ...initialValues,
        }}
        {...formProps}
      >
        {/* Campo registrado en el form para vehicleToDto (llenado vía cascada o autocomplete) */}
        <Form.Item name={VEHICLE_FIELDS.CATEGORY} hidden><Input /></Form.Item>

        <Row gutter={16} align='top'>
          {/* 1. Placa */}
          <Col xs={24} md={12}>
            <Form.Item
              label='Placa'
              tooltip={{
                title: (
                  <>
                    <Text style={{ color: 'inherit' }}>Formatos permitidos:</Text>
                    <ul style={{ paddingLeft: '1.5em', margin: 0 }}>
                      <li>ABC123</li>
                      <li>CD1234, CC1234, AT1234, OI1234</li>
                      <li>ABC12, ABC12D</li>
                      <li>R12345, S12345</li>
                    </ul>
                  </>
                ),
                styles: { root: { maxWidth: '30em' } },
              }}
              required
            >
              <Form.Item
                name={VEHICLE_FIELDS.PLATE}
                rules={[
                  { required: hasPlate },
                  { validator: (_, value) => validatePlate(value) },
                  {
                    validator: () => {
                      if (hasPlate && plate && !plateReady) {
                        return Promise.reject('No se ha consultado la placa aún');
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                validateTrigger={[]}
                noStyle
              >
                <InputPlate
                  onVehicleByPlateQuery={handleVehicleByPlateQuery}
                  validator={validatePlate}
                  disabled={!hasPlate || !!initialValues}
                />
              </Form.Item>
              <Form.Item
                name={VEHICLE_FIELDS.HAS_PLATE}
                valuePropName='checked'
                getValueProps={(val) => ({ checked: !val })}
                normalize={(checked) => !checked}
                noStyle
                hidden={!!initialValues}
              >
                <Checkbox style={{ marginTop: 4 }}>No conozco la placa</Checkbox>
              </Form.Item>
            </Form.Item>
          </Col>

          {/* 2. Código Fasecolda */}
          <Col xs={24} md={12}>
            <Form.Item label='Código Fasecolda' required>
              <Form.Item
                name={VEHICLE_FIELDS.FASECOLDA_CODE}
                rules={[{ required: true }]}
                validateTrigger={[]}
                noStyle
              >
                <InputFasecoldaCode
                  onVehicleByFasecoldaCodeQuery={handleVehicleByFasecoldaCodeQuery}
                  disabled={unknownFasecoldaCode || (!plateReady && hasPlate)}
                />
              </Form.Item>
              <Form.Item
                name={AUX_VEHICLE_FIELDS.UNKNOWN_FASECOLDA_CODE}
                valuePropName='checked'
                noStyle
              >
                <Checkbox
                  style={{ marginTop: 4 }}
                  disabled={!plateReady && hasPlate}
                >
                  No conozco el código Fasecolda
                </Checkbox>
              </Form.Item>
            </Form.Item>
          </Col>

          {/* 3. Categoría */}
          <Col xs={24} md={12}>
            <Form.Item
              label='Categoría'
              name={AUX_VEHICLE_FIELDS.CATEGORY_ID}
              rules={[{ required: true }]}
              extra={categoriesQuery.isError ? <Text type='danger' style={{ fontSize: '0.9em' }}>Error al cargar las categorías. Intente de nuevo.</Text> : undefined}
            >
              <SelectPlus
                options={categoryOptions}
                placeholder='Categoría'
                loading={categoriesQuery.isFetching}
                disabled={!unknownFasecoldaCode || !fieldReady(true)}
              />
            </Form.Item>
          </Col>

          {/* 4. Estado del vehículo */}
          <Col xs={24} md={12}>
            <Form.Item
              label='Estado del vehículo'
              name={VEHICLE_FIELDS.IS_NEW}
              rules={[{ required: true }]}
            >
              <Radio.Group
                options={[
                  { label: 'Usado', value: false },
                  { label: 'Nuevo', value: true },
                ]}
                optionType='button'
                buttonStyle='solid'
                block
                disabled={!fieldReady(!!categoryId)}
              />
            </Form.Item>
          </Col>

          {/* 5. Modelo */}
          <Col xs={24} md={12}>
            <Form.Item
              label='Modelo'
              name={VEHICLE_FIELDS.MODEL}
              rules={[{ required: true }]}
              extra={modelsQuery.isError ? <Text type='danger' style={{ fontSize: '0.9em' }}>Error al cargar los modelos. Intente de nuevo.</Text> : undefined}
            >
              <SelectPlus
                options={modelOptions}
                placeholder='Modelo'
                filterSort={(a, b) => Number(b.label) - Number(a.label)}
                loading={modelsQuery.isFetching}
                disabled={!fieldReady(!!categoryId && isStateSelected)}
              />
            </Form.Item>
          </Col>

          {/* 6. Marca */}
          <Col xs={24} md={12}>
            <Form.Item
              label='Marca'
              name={VEHICLE_FIELDS.BRAND}
              rules={[{ required: true }]}
            >
              <SelectPlus
                options={brandOptions}
                placeholder='Marca'
                loading={brandsQuery.isFetching}
                disabled={unlockedByFasecolda || !fieldReady(!!model)}
              />
            </Form.Item>
          </Col>

          {/* 7. Referencia */}
          <Col xs={24} md={12}>
            <Form.Item
              label='Referencia'
              name={VEHICLE_FIELDS.LINE}
              rules={[{ required: true }]}
              extra={vehiclesSearchQuery.isError ? <Text type='danger' style={{ fontSize: '0.9em' }}>Error al cargar las referencias. Intente de nuevo.</Text> : undefined}
            >
              <SelectPlus
                options={lineOptions}
                placeholder='Referencia'
                loading={vehiclesSearchQuery.isFetching}
                disabled={unlockedByFasecolda || !fieldReady(!!brand)}
              />
            </Form.Item>
          </Col>

          {/* 8. Clase */}
          <Col xs={24} md={12}>
            <Form.Item
              label='Clase'
              name={VEHICLE_FIELDS.CLASS}
              rules={[{ required: true }]}
              required={false}
            >
              <Input readOnly placeholder='Clase' />
            </Form.Item>
          </Col>

          {/* 9. Ciudad de circulación */}
          <Col xs={24} md={12}>
            <Form.Item
              label='Ciudad de circulación'
              name={VEHICLE_FIELDS.CIRCULATION_CITY_ID}
              rules={[{ required: true }]}
            >
              <SelectMunicipality
                placeholder='Ciudad de circulación'
                onChange={onCirculationCityChange}
                disabled={!line}
              />
            </Form.Item>
          </Col>

          {/* 10. Tipo de servicio */}
          <Col xs={24} md={12}>
            <Form.Item
              label='Tipo de servicio'
              name={VEHICLE_FIELDS.SERVICE}
              rules={[{ required: true }]}
            >
              <SelectServiceType disabled={!line} />
            </Form.Item>
          </Col>

          {/* 11. Valor asegurado */}
          <Col xs={24} md={12}>
            <Form.Item
              label='Valor asegurado'
              name={VEHICLE_FIELDS.INSURED_VALUE}
              rules={[{ required: true }]}
            >
              <InputCurrency
                placeholder='Ingrese valor asegurado'
                numberType='positive'
                disabled={!line}
              />
            </Form.Item>
          </Col>

          {/* 12. Valor de accesorios */}
          <Col xs={24} md={12}>
            <Form.Item
              label='Valor de accesorios'
              name={VEHICLE_FIELDS.ACCESSORIES_VALUE}
              rules={[{ required: true }]}
            >
              <InputCurrency
                placeholder='Ingresar valor de accesorios'
                numberType='positive'
                disabled={!line}
              />
            </Form.Item>
          </Col>

          {/* 13. ¿Es utilitario? */}
          <Col xs={24}>
            <Form.Item
              label='¿Es utilitario?'
              name={VEHICLE_FIELDS.IS_UTILITARIAN}
              rules={[{ required: true }]}
            >
              <Radio.Group
                options={[
                  { label: 'Sí', value: true },
                  { label: 'No', value: false },
                ]}
                optionType='button'
                buttonStyle='solid'
                disabled={!line}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Card>
  );
};

// Enrutador: formulario de fallback (API Fasecolda del backend, desactualizada) vs formulario
// actual (vehicle-data-service). Único punto de contacto entre ambas implementaciones.
// eslint-disable-next-line react/prop-types
export const VehicleForm = (props) => {
  const { fasecolda_fallback: fasecoldaFallbackFlag } = useFlags(['fasecolda_fallback']);

  // Por defecto queda el formulario actual: el fallback solo se usa cuando alguien enciende
  // el flag porque el servicio actual falló. Mientras Flagsmith responde, o si no responde,
  // también aplica este default.
  const isFallbackEnabled = fasecoldaFallbackFlag?.enabled ?? false;

  return isFallbackEnabled ? <VehicleFormFallback {...props} /> : <VehicleFormCurrent {...props} />;
};
